/**
 * Quantity stepper.
 *
 * Delegated from document so it needs no wiring per page. The minus button
 * disables at the input's own `min` rather than at a hard-coded 1, so a part
 * that ever ships in pairs keeps working.
 *
 * `input` is dispatched after a step so anything listening to the field — a
 * future price-per-quantity readout, say — sees the change; setting .value in
 * code fires nothing on its own.
 */
document.addEventListener('click', (event) => {
  const step = event.target.closest('[data-quantity-step]');
  if (!step) return;

  const control = step.closest('[data-quantity]');
  const input = control?.querySelector('[data-quantity-input]');
  if (!input) return;

  const min = Number(input.min) || 1;
  const next = Math.max(min, (Number(input.value) || min) + Number(step.dataset.quantityStep));

  input.value = String(next);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

/** Keeps the minus button honest, including after typing straight into the field. */
document.addEventListener('input', (event) => {
  const input = event.target.closest('[data-quantity-input]');
  if (!input) return;

  const min = Number(input.min) || 1;
  const down = input.closest('[data-quantity]')?.querySelector('[data-quantity-step="-1"]');
  if (down) down.disabled = (Number(input.value) || min) <= min;
});

/**
 * Keeps the price block, buy button and MPN in step with the chosen variant.
 *
 * Most parts in this catalogue are single-variant, in which case the form
 * carries a hidden input and none of this runs. Delegated from document so the
 * one multi-variant page in a thousand needs no per-element wiring.
 */
document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-variant-select]');
  if (!select) return;

  const option = select.selectedOptions[0];
  if (!option) return;

  const form = select.closest('form');
  const scope = form?.parentElement ?? document;

  // :not(option) — the select's own options carry data-price/data-compare
  // too, and come first in the column.
  const available = option.dataset.available === 'true';
  const compareCents = Number(option.dataset.compareCents ?? 0);
  const priceCents = Number(option.dataset.priceCents ?? 0);

  // Out of stock shows the compare-at price when there is one (Lucas,
  // 2026-10-14, request 132): the sale price is not on offer.
  const price = scope.querySelector('[data-price]:not(option)');
  if (price) price.textContent = (!available && compareCents > 0 ? option.dataset.compare : option.dataset.price) ?? '';

  // MSRP shows only when it is genuinely above the selling price. On this
  // catalogue it is usually equal or lower, and striking through a number that
  // isn't a saving reads as a fake discount.
  const compare = scope.querySelector('[data-compare]:not(option)');
  if (compare) {
    const saving = available && compareCents > priceCents;
    compare.textContent = saving ? `MSRP ${option.dataset.compare}` : '';
    compare.hidden = !saving;
  }

  const button = form?.querySelector('[data-add-button]');
  if (button) {
    button.disabled = !available;
    button.textContent = available ? 'Add to cart' : 'Out of stock';
  }

  // Out of stock: the buying controls go — quantity, buy row, shipping line —
  // and the back-in-stock offer takes their place, keyed to this variant.
  for (const el of scope.querySelectorAll('[data-buy-when-available]')) el.hidden = !available;
  const unavailable = scope.querySelector('[data-buy-unavailable]');
  if (unavailable) unavailable.hidden = available;
  // Klaviyo's back-in-stock slot carries the chosen variant (request 171).
  const bis = scope.querySelector('[data-klaviyo-bis]');
  if (bis) bis.dataset.variantId = option.value;

  const mpn = scope.querySelector('[data-mpn]');
  // The MPN is the SKU past its underscore ("12POL_2872726" → "2872726"); it
  // is a link to the search for the number (request 167), kept in step.
  if (mpn && option.dataset.sku) {
    const bare = option.dataset.sku.split('_').pop();
    mpn.textContent = bare;
    if (mpn.tagName === 'A') mpn.href = `/search?q=${encodeURIComponent(bare)}`;
  }

  paintPicker(select);
});

/**
 * The option picker (chips and swatches) over the variant select: the
 * select stays the source of truth, the picker sets it and follows it.
 *
 * Each <option> carries its variant's values (data-options, "S||Black"); a
 * group's button is pressed when the chosen variant has its value, and
 * marked out (data-out) when no variant in stock has that value together
 * with the other groups' chosen values. Choosing a value picks the variant
 * with it and the other current values, or — when that combination does
 * not exist — the first variant that has it, in stock first.
 */
function variantsOf(select) {
  return [...select.options].map((o) => ({
    id: o.value,
    options: (o.dataset.options || '').split('||'),
    available: o.dataset.available === 'true',
  }));
}

function pickerOf(select) {
  const form = select.closest('form');
  return form ? form.querySelector('[data-variant-picker]') : null;
}

function paintPicker(select) {
  const picker = pickerOf(select);
  if (!picker) return;
  const current = (select.selectedOptions[0]?.dataset.options || '').split('||');
  const variants = variantsOf(select);
  picker.querySelectorAll('[data-option-group]').forEach((group, gi) => {
    for (const button of group.querySelectorAll('[data-option-value]')) {
      const value = button.dataset.optionValue;
      button.setAttribute('aria-pressed', String(current[gi] === value));
      const inStock = variants.some((v) => v.available && v.options[gi] === value
        && v.options.every((o, i) => i === gi || o === current[i]));
      button.toggleAttribute('data-out', !inStock);
    }
    const chosen = group.querySelector('[data-option-chosen]');
    if (chosen) chosen.textContent = current[gi] || '';
  });
  picker.setAttribute('data-ready', '');
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-option-value]');
  if (!button) return;
  const picker = button.closest('[data-variant-picker]');
  const select = picker?.closest('form')?.querySelector('[data-variant-select]');
  if (!select) return;
  const groups = [...picker.querySelectorAll('[data-option-group]')];
  const gi = groups.indexOf(button.closest('[data-option-group]'));
  const want = (select.selectedOptions[0]?.dataset.options || '').split('||');
  want[gi] = button.dataset.optionValue;
  const variants = variantsOf(select);
  const exact = variants.find((v) => v.options.every((o, i) => o === want[i]));
  const withValue = variants.filter((v) => v.options[gi] === want[gi]);
  const pick = exact || withValue.find((v) => v.available) || withValue[0];
  if (!pick || pick.id === select.value) { paintPicker(select); return; }
  select.value = pick.id;
  select.dispatchEvent(new Event('change', { bubbles: true }));
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', pick.id);
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  } catch { /* the page still shows the chosen variant */ }
});

/**
 * Klaviyo's back-in-stock button (request 172). Klaviyo places it only
 * "stacked" under the add-to-cart button or in its place, and that button is
 * hidden while a part is out of stock, so theirs would hide with it. Whatever
 * Klaviyo injects inside the product form is moved into the out-of-stock
 * slot, where it shows under "Out of stock" dressed as the theme's button.
 * (Lucas should choose "Stacked": "Replace" removes the add-to-cart button
 * the in-stock variants need.)
 */
function adoptKlaviyo() {
  const form = document.querySelector('[data-product-form]');
  const slot = form && form.querySelector('[data-klaviyo-bis]');
  if (!slot || !window.MutationObserver) return;
  const isKlaviyo = (el) => el.nodeType === 1 && el !== slot && !slot.contains(el)
    && /klaviyo/i.test(`${el.getAttribute('class') || ''} ${el.id || ''}`);
  const adopt = (node) => {
    if (node.nodeType !== 1) return;
    if (isKlaviyo(node)) { slot.appendChild(node); return; }
    for (const el of node.querySelectorAll('[class*="klaviyo" i], [id*="klaviyo" i]')) if (isKlaviyo(el)) slot.appendChild(el);
  };
  adopt(form);
  new MutationObserver((records) => {
    for (const record of records) for (const node of record.addedNodes) adopt(node);
  }).observe(form, { childList: true, subtree: true });
}

function bootPickers() {
  document.querySelectorAll('[data-variant-select]').forEach(paintPicker);
}
function boot() { bootPickers(); adoptKlaviyo(); }
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

/**
 * Add to cart in place.
 *
 * The form still posts to /cart/add on its own, so without JS the page simply
 * navigates to the cart. With JS, cart.js makes the request and announces it,
 * the drawer opens, and the button reports back — the customer stays on the
 * product. A cart error (already holding all the stock, say) is shown under
 * the buy row in Shopify's own words rather than as a navigation to an error.
 *
 * Properties (`properties[Name]` inputs) are carried over so a line-item
 * property form keeps working if one is ever added.
 */
document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-product-form]');
  if (!form || !window.Cart) return;

  event.preventDefault();

  const button = form.querySelector('[data-add-button]');
  if (button?.dataset.state === 'adding') return;

  const data = new FormData(form);
  const item = {
    id: Number(data.get('id')),
    quantity: Number(data.get('quantity') || 1),
  };
  const properties = {};
  for (const [key, value] of data.entries()) {
    const match = /^properties\[(.+)\]$/.exec(key);
    if (match) properties[match[1]] = value;
  }
  if (Object.keys(properties).length) item.properties = properties;

  const error = form.parentElement?.querySelector('[data-add-error]') ?? form.querySelector('[data-add-error]');
  if (error) {
    error.hidden = true;
    error.textContent = '';
  }

  const original = button?.textContent ?? 'Add to cart';
  if (button) {
    button.dataset.state = 'adding';
    button.textContent = 'Adding…';
  }

  try {
    await window.Cart.add([item]);
    if (button) {
      button.dataset.state = 'added';
      button.textContent = 'Added ✓';
    }
    setTimeout(() => {
      if (!button || button.dataset.state !== 'added') return;
      delete button.dataset.state;
      button.textContent = original;
    }, 2000);
  } catch (err) {
    if (button) {
      delete button.dataset.state;
      button.textContent = original;
    }
    if (error) {
      error.textContent = err.message;
      error.hidden = false;
    } else {
      console.error('[product-form]', err);
    }
  }
});
